import { AppBar, Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Toolbar, Typography, Menu, MenuItem, Avatar, Divider } from '@mui/material'
import MenuIcon from '@mui/icons-material/Menu'
import HomeIcon from '@mui/icons-material/Home'
import FolderIcon from '@mui/icons-material/Folder'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import ImageIcon from '@mui/icons-material/Image'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import AssignmentIcon from '@mui/icons-material/Assignment'
import QuizIcon from '@mui/icons-material/Quiz'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/theme/ThemeProvider'
import { useAuth } from '@/contexts/AuthContext'
import { useEdition } from '@/hooks/useEdition'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import LanguageSwitcher from '@/components/LanguageSwitcher'
import EnvBadge from '@/components/EnvBadge'
import { supportMailto } from '@/config/contact'

type AppLayoutProps = {
  children: React.ReactNode
}

const drawerWidth = 240

export default function AppLayout({ children }: AppLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const { mode, toggle } = useThemeMode()
  const { user, logout, currentView, switchView } = useAuth()
  const { isPro } = useEdition()
  const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation('layout')
  const { t: tNav } = useTranslation('dashboard')

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleProfileMenuClose = () => {
    setAnchorEl(null)
  }

  const handleProfile = () => {
    handleProfileMenuClose()
    navigate('/profile')
  }

  const handleLogout = async () => {
    handleProfileMenuClose()
    await logout()
    navigate('/login')
  }

  const handleSwitchView = () => {
    handleProfileMenuClose()
    switchView(currentView === 'researcher' ? 'respondent' : 'researcher')
    navigate('/')
  }

  const closeMobileDrawer = () => setMobileOpen(false)

  const drawerContent = (
    <>
      <Toolbar />
      <List>
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/" selected={location.pathname === '/'} onClick={closeMobileDrawer}>
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            <ListItemText primary={t('home')} />
          </ListItemButton>
        </ListItem>
        {currentView === 'researcher' && (
          <>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/projects" selected={location.pathname === '/projects'} onClick={closeMobileDrawer}>
                <ListItemIcon>
                  <FolderIcon />
                </ListItemIcon>
                <ListItemText primary={t('projects')} />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton component={Link} to="/camera" selected={location.pathname === '/camera'} onClick={closeMobileDrawer}>
                <ListItemIcon>
                  <PhotoCameraIcon />
                </ListItemIcon>
                <ListItemText primary={t('camera')} />
              </ListItemButton>
            </ListItem>
            {/* Image analysis and the template library are Pro rooms — this
                older drawer still links to them, and for FaceWise the route
                guard would bounce the click back to the dashboard. */}
            {isPro && (
              <>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/image-analysis" selected={location.pathname === '/image-analysis'} onClick={closeMobileDrawer}>
                    <ListItemIcon>
                      <ImageIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('analyzeImage')} />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding>
                  <ListItemButton component={Link} to="/questionnaires" selected={location.pathname.startsWith('/questionnaires')} onClick={closeMobileDrawer}>
                    <ListItemIcon>
                      <QuizIcon />
                    </ListItemIcon>
                    <ListItemText primary={t('questionnaires')} />
                  </ListItemButton>
                </ListItem>
              </>
            )}
          </>
        )}
        <ListItem disablePadding>
          <ListItemButton component={Link} to="/participant/dashboard" selected={location.pathname.startsWith('/participant')} onClick={closeMobileDrawer}>
            <ListItemIcon>
              <AssignmentIcon />
            </ListItemIcon>
            <ListItemText primary={t('myAssignments')} />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  )

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="toggle navigation"
            onClick={() => setMobileOpen((v) => !v)}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, minWidth: 0, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>FaceWise by</Box>
            <img src="/affexy-logo.png" alt="Affexy" style={{ height: 24 }} />
            <EnvBadge />
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageSwitcher />
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user?.first_name && user?.last_name
                ? `${user.first_name} ${user.last_name}`
                : user?.email}
            </Typography>
            {/* Current-view indicator (researcher/respondent) */}
            <Typography
              variant="caption"
              sx={{
                display: { xs: 'none', sm: 'block' },
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: 'rgba(255,255,255,.16)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {currentView === 'researcher' ? tNav('nav.roleResearcher') : tNav('nav.roleRespondent')}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.first_name ? user.first_name[0].toUpperCase() : user?.email?.[0].toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleProfileMenuClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleProfile}>
                <ListItemIcon>
                  <PersonIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('profile')}</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleSwitchView}>
                <ListItemIcon>
                  <SwapHorizIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {currentView === 'researcher' ? tNav('nav.switchToRespondent') : tNav('nav.switchToResearcher')}
                </ListItemText>
              </MenuItem>
              <MenuItem
                component="a"
                href={supportMailto(t('helpMailSubject'))}
                onClick={handleProfileMenuClose}
              >
                <ListItemIcon>
                  <HelpOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('help')}</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('logout')}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>

          <IconButton color="inherit" onClick={toggle} aria-label="toggle theme">
            {mode === 'dark' ? '🌙' : '🌞'}
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        {/* Temporary drawer — mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={closeMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
        {/* Permanent drawer — desktop */}
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, md: 3 }, minWidth: 0, width: { md: `calc(100% - ${drawerWidth}px)` } }}>
        <Toolbar />
        {children}
      </Box>
    </Box>
  )
}
